import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  CPT_CATEGORIES,
  CPT_MODIFIERS,
  CPT_PLACES_OF_SERVICE,
  blankCptCode,
  filterCptCodes,
  normalizeCptCode,
  validateCptCode,
  type CptCode,
  type CptCodeDraft,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import { useSession, canManage } from '../auth/session.js';
import { Modal } from './Modal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { MultiDd } from './Dropdown.js';
import { SaveButton, errNotice, runSave, type SaveNotice } from './SaveToast.js';
import { CPT_FIX_FIELDS, cptErrText, cptSavedText } from '../lib/saveCards.js';

/**
 * calendar#38 - Manage Settings > CPT codes (Chase's CPT_CODES_MODAL_10-08_V4). The practice's
 * one code list is the Calendar's chargemaster (Jay 2026-10-09): this reads it through
 * GET /cpt-codes and saves through the Calendar (POST / PUT / DELETE /cpt-codes, forwarded with
 * the user's token). The Calendar's Settings > CPT codes edits the same rows, and RCM's claims and
 * the progress-note CPT read them.
 *
 * ehr#681 follow-up: Create code / Save changes reads "Saving…" for at least 2 s, then the drawer's
 * save card ("CPT code 90837 saved", or "Not saved" with the reason) sits above it, in place of
 * the old flash text in the footer. The footer line keeps Delete's result.
 */

interface CptList {
  available: boolean;
  codes: CptCode[];
}
type Errs = ReturnType<typeof validateCptCode>;
type Ask = { kind: 'unsaved'; then: () => void } | { kind: 'delete'; code: CptCode } | null;

const toDraft = (c: CptCode): CptCodeDraft => ({
  ...c,
  modifiers: [...c.modifiers],
  placesOfService: [...c.placesOfService],
});
const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

/**
 * calendar#38 (Chase, 10-10): Allowed modifiers / places of service are the Credify multi-select
 * (MultiDd): checkboxes, the list stays open while you tick, and closes when you click elsewhere.
 * It replaced a native select that added one code per pick. Read-only users see the picks as chips.
 */
function Picker({
  label,
  options,
  value,
  disabled,
  onChange,
  addLabel,
  hint,
}: {
  label: string;
  options: readonly { code: string; label: string }[];
  value: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
  addLabel: string;
  hint: string;
}) {
  return (
    <div className="cptm-f full">
      <span className="cptm-lbl">{label}</span>
      {disabled ? (
        <div className="cptm-chips" role="group" aria-label={label}>
          {value.map((c) => (
            <span className="cptm-chip" key={c}>
              <b>{c}</b> {options.find((o) => o.code === c)?.label ?? ''}
            </span>
          ))}
        </div>
      ) : (
        <MultiDd
          ariaLabel={label}
          placeholder={`${addLabel}…`}
          values={value}
          options={options.map((o) => ({
            value: o.code,
            label: `${o.code} — ${o.label}`,
            search: o.label,
          }))}
          onChange={onChange}
          maxLabel={3}
          clearable
          floating
        />
      )}
      <span className="cptm-hint">{hint}</span>
    </div>
  );
}

export function CptCodesModal({ onClose }: { onClose: () => void }) {
  const { user } = useSession();
  const canEdit = canManage(user?.role);
  const [list, setList] = useState<CptList | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState<CptCodeDraft>(blankCptCode);
  const [errs, setErrs] = useState<Errs>({});
  const [dirty, setDirty] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [cat, setCat] = useState('all');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ text: string; bad?: boolean } | null>(null);
  const [saveNote, setSaveNote] = useState<SaveNotice | null>(null);
  const [ask, setAsk] = useState<Ask>(null);

  useEffect(() => {
    let live = true;
    api
      .get<CptList>('/cpt-codes')
      .then((r) => live && setList(r))
      .catch((e) => live && setLoadErr(msgOf(e)));
    return () => {
      live = false;
    };
  }, []);

  const codes = useMemo(() => list?.codes ?? [], [list]);
  const shown = filterCptCodes(codes, q, status, cat);
  const cats = useMemo(() => {
    const out: string[] = [...CPT_CATEGORIES];
    codes.forEach((c) => c.category && !out.includes(c.category) && out.push(c.category));
    return out;
  }, [codes]);
  const ro = !canEdit || !list?.available;
  const isNew = !form.id;

  const load = (d: CptCodeDraft) => {
    setForm(d);
    setErrs({});
    setDirty(false);
  };
  const guard = (then: () => void) => (dirty ? setAsk({ kind: 'unsaved', then }) : then());
  const set = <K extends keyof CptCodeDraft>(k: K, v: CptCodeDraft[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
    setNote(null);
    if (k in errs) setErrs((e) => ({ ...e, [k]: undefined }));
  };

  const save = (then?: () => void) => {
    if (ro || busy) return;
    const draft = { ...form, code: normalizeCptCode(form.code) };
    const e = validateCptCode(draft, codes);
    setErrs(e);
    setNote(null);
    if (Object.values(e).some(Boolean)) {
      setSaveNote(errNotice(CPT_FIX_FIELDS));
      return;
    }
    setSaveNote(null);
    const body = {
      code: draft.code,
      shortDescription: draft.shortDescription.trim(),
      longDescription: draft.longDescription.trim(),
      category: draft.category,
      defaultFee: draft.defaultFee,
      typicalMinutes: draft.typicalMinutes,
      modifiers: draft.modifiers,
      placesOfService: draft.placesOfService,
      active: draft.active,
    };
    setBusy(true);
    const req = draft.id
      ? api.put<CptList>(`/cpt-codes/${draft.id}`, body)
      : api.post<CptList & { id: string }>('/cpt-codes', body);
    // the list and form take the server's answer at once; the button and card wait 2 s
    void runSave(
      req.then((r) => {
        setList({ available: r.available, codes: r.codes });
        const id = draft.id ?? ('id' in r ? (r as { id: string }).id : null);
        const saved = r.codes.find((c) => c.id === id) ?? r.codes.find((c) => c.code === body.code);
        load(saved ? toDraft(saved) : blankCptCode());
        return r;
      }),
      { ok: cptSavedText(body.code, !draft.id), err: cptErrText },
    ).then((r) => {
      setBusy(false);
      setSaveNote(r.notice);
      if (r.ok) then?.();
      else {
        const m = msgOf(r.error);
        if (/already in your code list/.test(m)) setErrs({ code: m });
      }
    });
  };

  const remove = (c: CptCode) => {
    setBusy(true);
    setSaveNote(null);
    api
      .del<CptList>(`/cpt-codes/${c.id}`)
      .then((r) => {
        setList(r);
        load(blankCptCode());
        setNote({ text: `Deleted ${c.code}` });
      })
      .catch((err) => setNote({ text: msgOf(err), bad: true }))
      .finally(() => {
        setBusy(false);
        setAsk(null);
      });
  };

  const requestClose = () => {
    if (ask) return;
    guard(onClose);
  };
  const fld = (
    key: keyof Errs,
    label: string,
    input: ReactNode,
    opts: { full?: boolean; optional?: boolean } = {},
  ) => (
    <div className={`cptm-f${opts.full ? ' full' : ''}`} data-fld={key}>
      <label htmlFor={`cptm-${key}`}>
        {label}
        {opts.optional ? <span className="opt"> (optional)</span> : <span className="req">*</span>}
      </label>
      {input}
      {errs[key] && <div className="cptm-msg">{errs[key]}</div>}
    </div>
  );
  const bad = (k: keyof Errs) => (errs[k] ? ' bad' : '');

  return (
    <Modal title="CPT codes" onClose={requestClose} className="cptm-modal">
      <p className="cptm-sub">
        Every procedure code your practice bills. Pick a code to edit it, or add a new one. This is
        the same list as the Calendar&rsquo;s Settings &gt; CPT codes.
      </p>
      {loadErr && <p className="cptm-banner bad">{loadErr}</p>}
      {list && !list.available && (
        <p className="cptm-banner">
          The code list lives in the Calendar, which hasn&rsquo;t been updated for it yet. It will
          show here once it is.
        </p>
      )}
      {list?.available && !canEdit && (
        <p className="cptm-banner">Only an admin or manager can change the code list.</p>
      )}
      <div className="cptm-split">
        <section className="cptm-left" aria-label="Existing CPT codes">
          <div className="cptm-tools">
            <div className="cptm-row">
              <input
                className="cptm-inp"
                type="search"
                id="cptm-search"
                placeholder="Search by code or description"
                aria-label="Search CPT codes"
                autoComplete="off"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {!ro && (
                <button
                  type="button"
                  className="btn primary"
                  id="cptm-new"
                  onClick={() => guard(() => load(blankCptCode()))}
                >
                  + New code
                </button>
              )}
            </div>
            <div className="cptm-row">
              <select
                className="cptm-inp"
                aria-label="Filter by status"
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
              >
                <option value="all">All statuses</option>
                <option value="active">Active only</option>
                <option value="inactive">Inactive only</option>
              </select>
              <select
                className="cptm-inp"
                aria-label="Filter by category"
                value={cat}
                onChange={(e) => setCat(e.target.value)}
              >
                <option value="all">All categories</option>
                {cats.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="cptm-list" role="list">
            {!list && !loadErr && <div className="cptm-empty">Loading…</div>}
            {list && shown.length === 0 && (
              <div className="cptm-empty">{codes.length ? 'No codes match.' : 'No codes yet.'}</div>
            )}
            {shown.map((c) => (
              <button
                type="button"
                key={c.id}
                role="listitem"
                data-cpt={c.code}
                className={`cptm-item${form.id === c.id ? ' on' : ''}`}
                onClick={() => form.id !== c.id && guard(() => load(toDraft(c)))}
              >
                <span className="t">
                  <span className="c">{c.code}</span>
                  <span className="d">{c.shortDescription}</span>
                  {!c.active && <span className="cptm-off">Inactive</span>}
                </span>
                <span className="m">
                  {c.category || 'No category'} · {money(c.defaultFee)}
                  {c.typicalMinutes ? ` · ${c.typicalMinutes} min` : ''}
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="cptm-right" aria-labelledby="cptm-ftitle">
          <div className="cptm-fhd">
            <h4 id="cptm-ftitle">{isNew ? 'New CPT code' : form.code}</h4>
            {!isNew && <span>{form.shortDescription}</span>}
          </div>
          <form
            className="cptm-form"
            noValidate
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
          >
            {fld(
              'code',
              'Code',
              <input
                id="cptm-code"
                className={`cptm-inp${bad('code')}`}
                maxLength={5}
                placeholder="e.g. 90837"
                value={form.code}
                disabled={ro}
                onChange={(e) => set('code', e.target.value)}
              />,
            )}
            {fld(
              'category',
              'Category',
              <select
                id="cptm-category"
                className={`cptm-inp${bad('category')}`}
                value={form.category}
                disabled={ro}
                onChange={(e) => set('category', e.target.value)}
              >
                <option value="">Pick a category</option>
                {cats.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>,
            )}
            {fld(
              'shortDescription',
              'Short description',
              <input
                id="cptm-shortDescription"
                className={`cptm-inp${bad('shortDescription')}`}
                maxLength={60}
                placeholder="What staff see on schedules and claims"
                value={form.shortDescription}
                disabled={ro}
                onChange={(e) => set('shortDescription', e.target.value)}
              />,
              { full: true },
            )}
            <div className="cptm-f full">
              <label htmlFor="cptm-longDescription">
                Long description<span className="opt"> (optional)</span>
              </label>
              <textarea
                id="cptm-longDescription"
                className="cptm-inp"
                rows={3}
                placeholder="Full description, documentation notes, or billing reminders"
                value={form.longDescription}
                disabled={ro}
                onChange={(e) => set('longDescription', e.target.value)}
              />
            </div>
            {fld(
              'defaultFee',
              'Default fee',
              <div className="cptm-money">
                <span>$</span>
                <input
                  id="cptm-defaultFee"
                  className={`cptm-inp${bad('defaultFee')}`}
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0.00"
                  value={form.defaultFee ?? ''}
                  disabled={ro}
                  onChange={(e) =>
                    set('defaultFee', e.target.value === '' ? null : Number(e.target.value))
                  }
                />
              </div>,
            )}
            {fld(
              'typicalMinutes',
              'Typical minutes',
              <input
                id="cptm-typicalMinutes"
                className={`cptm-inp${bad('typicalMinutes')}`}
                type="number"
                min={1}
                step={1}
                placeholder="e.g. 53"
                value={form.typicalMinutes ?? ''}
                disabled={ro}
                onChange={(e) =>
                  set('typicalMinutes', e.target.value === '' ? null : Number(e.target.value))
                }
              />,
              { optional: true },
            )}
            <Picker
              label="Allowed modifiers"
              addLabel="Add a modifier"
              options={CPT_MODIFIERS}
              value={form.modifiers}
              disabled={ro}
              onChange={(v) => set('modifiers', v)}
              hint="Leave empty to allow any modifier."
            />
            <Picker
              label="Allowed places of service"
              addLabel="Add a place of service"
              options={CPT_PLACES_OF_SERVICE}
              value={form.placesOfService}
              disabled={ro}
              onChange={(v) => set('placesOfService', v)}
              hint="Leave empty to allow any place of service."
            />
            <div className="cptm-f full">
              <div className="cptm-switch">
                <div>
                  <div className="t">Active</div>
                  <div className="cptm-hint">
                    Active codes can be picked on appointments, notes and claims.
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  id="cptm-active"
                  aria-label="Active"
                  aria-checked={form.active}
                  className={`cptm-sw${form.active ? ' on' : ''}`}
                  disabled={ro}
                  onClick={() => set('active', !form.active)}
                />
              </div>
            </div>
          </form>
          <div className="cptm-foot save-host">
            {!ro && !isNew && (
              <button
                type="button"
                className="btn danger"
                id="cptm-delete"
                disabled={busy}
                onClick={() => {
                  const c = codes.find((x) => x.id === form.id);
                  if (c) setAsk({ kind: 'delete', code: c });
                }}
              >
                Delete
              </button>
            )}
            <span className={`cptm-note${note?.bad ? ' bad' : ''}`} role="status">
              {note?.text ?? ''}
            </span>
            {!ro && (
              <>
                <button
                  type="button"
                  className="btn"
                  id="cptm-clear"
                  onClick={() => {
                    const c = codes.find((x) => x.id === form.id);
                    load(c ? toDraft(c) : blankCptCode());
                    setNote(null);
                  }}
                >
                  Clear
                </button>
                <SaveButton
                  id="cptm-save"
                  label={isNew ? 'Create code' : 'Save changes'}
                  busy={busy && !ask}
                  disabled={busy}
                  notice={saveNote}
                  onCloseNotice={() => setSaveNote(null)}
                  onClick={() => save()}
                />
              </>
            )}
          </div>
        </section>
      </div>

      {ask?.kind === 'unsaved' && (
        <ConfirmDialog
          message="You have unsaved changes in this CPT code. Save them before you leave?"
          confirmLabel="Save"
          danger={false}
          altLabel="Don’t Save"
          onAlt={() => {
            const then = ask.then;
            setAsk(null);
            setDirty(false);
            then();
          }}
          onConfirm={() => {
            const then = ask.then;
            setAsk(null);
            save(then);
          }}
          onClose={() => setAsk(null)}
        />
      )}
      {ask?.kind === 'delete' && (
        <ConfirmDialog
          message={
            <>
              Delete <b>{ask.code.code}</b> ({ask.code.shortDescription})? Past appointments keep
              the code they were booked with. To stop new use but keep it on file, turn it off
              instead.
            </>
          }
          confirmLabel="Delete"
          busy={busy}
          onConfirm={() => remove(ask.code)}
          onClose={() => setAsk(null)}
        />
      )}
    </Modal>
  );
}
